import { Button } from '@suscri/ui';
import { Coins, Lock, AlertTriangle } from 'lucide-react';
import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useToast } from '../lib/toast';
import { trpc } from '../lib/trpc';

// Flujo de acción premium (reglas de la propuesta de monetización):
// 1. Mostrar SIEMPRE el coste antes de consumir.
// 2. Consumir vía billing.consumeAction; si falla por plan o saldo, ofrecer upsell.
// 3. Solo tras el cobro se ejecuta la acción (el backend garantiza atomicidad del saldo).

type ModalState =
  | {
      kind: 'confirm';
      actionCode: string;
      name: string;
      cost: number;
      onExecute: () => Promise<void>;
    }
  | { kind: 'plan'; requiredPlan: string }
  | { kind: 'credits'; cost: number; balance: number }
  | null;

export function usePremiumAction() {
  const { t } = useTranslation();
  const [modal, setModal] = useState<ModalState>(null);
  const navigate = useNavigate();
  const toast = useToast();
  const utils = trpc.useUtils();
  const { data: actions } = trpc.billing.actions.useQuery();
  const consume = trpc.billing.consumeAction.useMutation();

  // Ejecución por defecto: cobra vía billing.consumeAction y luego ejecuta
  // onSuccess en el cliente (exportaciones, informes generados en el navegador).
  const run = useCallback(
    (actionCode: string, onSuccess: () => void) => {
      const action = actions?.find(a => a.code === actionCode);
      if (!action) return; // catálogo aún no cargado
      setModal({
        kind: 'confirm',
        actionCode,
        name: action.name,
        cost: action.cost,
        onExecute: async () => {
          await consume.mutateAsync({ actionCode });
          utils.billing.me.invalidate();
          utils.billing.creditHistory.invalidate();
          onSuccess();
        },
      });
    },
    [actions, consume, utils]
  );

  // Variante para mutaciones ya gateadas en servidor (premiumProcedure), donde
  // el propio endpoint verifica plan/saldo y cobra créditos de forma atómica
  // (p. ej. importación masiva). El cliente solo muestra el coste antes.
  const runMutation = useCallback(
    <T,>(
      actionCode: string,
      execute: () => Promise<T>,
      onSuccess: (result: T) => void
    ) => {
      const action = actions?.find(a => a.code === actionCode);
      if (!action) return;
      setModal({
        kind: 'confirm',
        actionCode,
        name: action.name,
        cost: action.cost,
        onExecute: async () => {
          const result = await execute();
          utils.billing.me.invalidate();
          utils.billing.creditHistory.invalidate();
          onSuccess(result);
        },
      });
    },
    [actions, utils]
  );

  const confirm = async () => {
    if (modal?.kind !== 'confirm') return;
    const { onExecute } = modal;
    try {
      await onExecute();
      setModal(null);
    } catch (error) {
      const message = error instanceof Error ? error.message : '';
      const planMatch = message.match(/PLAN_REQUIRED:(\w+)/);
      const creditsMatch = message.match(/INSUFFICIENT_CREDITS:(\d+):(\d+)/);
      if (planMatch?.[1]) {
        setModal({ kind: 'plan', requiredPlan: planMatch[1] });
      } else if (creditsMatch) {
        setModal({
          kind: 'credits',
          cost: Number(creditsMatch[1]),
          balance: Number(creditsMatch[2]),
        });
      } else {
        setModal(null);
        toast.error(t('No se pudo completar la acción. Inténtalo de nuevo.'));
      }
    }
  };

  const modalElement = modal ? (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={() => setModal(null)}
    >
      <div
        className="w-full max-w-md rounded-2xl bg-white dark:bg-slate-900 p-6 shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        {modal.kind === 'confirm' && (
          <>
            <div className="flex items-center gap-3 mb-4">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-950">
                <Coins className="h-5 w-5 text-amber-600 dark:text-amber-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {t(modal.name)}
              </h3>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
              {t('Esta acción consumirá')}{' '}
              <strong>{t('{{count}} créditos', { count: modal.cost })}</strong>{' '}
              {t('de tu saldo. ¿Quieres continuar?')}
            </p>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setModal(null)}>
                {t('Cancelar')}
              </Button>
              <Button onClick={confirm} disabled={consume.isPending}>
                {consume.isPending
                  ? t('Procesando...')
                  : t('Usar {{count}} créditos', { count: modal.cost })}
              </Button>
            </div>
          </>
        )}

        {modal.kind === 'plan' && (
          <>
            <div className="flex items-center gap-3 mb-4">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-violet-100 dark:bg-violet-950">
                <Lock className="h-5 w-5 text-violet-600 dark:text-violet-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {t('Función de plan superior')}
              </h3>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
              {t('Esta función está disponible a partir del plan')}{' '}
              <strong>{modal.requiredPlan}</strong>.{' '}
              {t('Mejora tu plan para desbloquearla.')}
            </p>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setModal(null)}>
                {t('Ahora no')}
              </Button>
              <Button
                onClick={() => {
                  setModal(null);
                  navigate('/plan');
                }}
              >
                {t('Ver planes')}
              </Button>
            </div>
          </>
        )}

        {modal.kind === 'credits' && (
          <>
            <div className="flex items-center gap-3 mb-4">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-100 dark:bg-red-950">
                <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {t('Créditos insuficientes')}
              </h3>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
              {t('Esta acción cuesta')}{' '}
              <strong>{t('{{count}} créditos', { count: modal.cost })}</strong>{' '}
              {t('y tu saldo actual es de')} <strong>{modal.balance}</strong>.{' '}
              {t('Puedes comprar un pack de créditos o mejorar tu plan.')}
            </p>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setModal(null)}>
                {t('Ahora no')}
              </Button>
              <Button
                onClick={() => {
                  setModal(null);
                  navigate('/plan');
                }}
              >
                {t('Conseguir créditos')}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  ) : null;

  return { run, runMutation, modalElement, actions };
}
